import { CircularProgress } from '@jouzen/ecom-components';
import { useQuery } from '@tanstack/react-query';
import kebabCase from 'lodash/kebabCase';
import { useMemo, useState } from 'react';

import { withQueryKey } from '@/app/[locale]/careers/_queries/withQueryKey';

import type { Department, Job } from '../types';
import CareersListAlert from './CareersListAlert';
import CareersListDepartment from './CareersListDepartment';
import CareersListFilters from './CareersListFilters';

export interface CareersListProps {
  readonly setDetailView: (dept: string, jobs: Job[]) => void;
}

const CareersList = ({ setDetailView }: CareersListProps): JSX.Element => {
  const [filter, setFilter] = useState<string>('');
  const { data: jobsData, isError, isLoading } = useQuery(withQueryKey(filter));

  const departments = useMemo((): Department[] => {
    if (!Array.isArray(jobsData?.jobs)) return [];

    // Derive unique list of departments
    const departmentNames = new Set<string>(
      jobsData.jobs
        .filter((job) => !!job.department)
        .map((job) => job.department),
    );

    const sortedDepartments = Array.from(departmentNames).sort((a, b) =>
      a.localeCompare(b),
    );

    return sortedDepartments.map((dept) => ({
      name: dept,
      count: jobsData.jobs.filter((job) => job.department === dept).length,
    }));
  }, [jobsData]);

  const handleFilterChange = (value: string) => {
    setFilter(value);
  };

  const handleDepartmentClick = (department: string) => {
    const jobs = jobsData!.jobs.filter(
      (job: Job) => job.department === department,
    );

    setDetailView(department, jobs);
  };

  return (
    <>
      <CareersListFilters
        onFilterChange={handleFilterChange}
        selectedFilter={filter}
      />
      <div className="divide-y divide-sandstone-500 border-b border-t-2 border-sandstone-500">
        {isLoading ? (
          <div
            className="flex justify-center py-24"
            data-cy="careers-list-loading"
          >
            <CircularProgress />
          </div>
        ) : (
          <>
            {isError ? (
              <CareersListAlert message="checkout_validate_params_exception" />
            ) : (
              <>
                {!jobsData?.jobs.length ? (
                  <CareersListAlert message="store_locator_no_results" />
                ) : (
                  departments.map((dept, i) => (
                    <CareersListDepartment
                      {...dept}
                      key={kebabCase(dept.name)}
                      index={i}
                      onClick={handleDepartmentClick}
                    />
                  ))
                )}
              </>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default CareersList;
